import { useState } from "react";
import type { Live } from "@/types/live";
import { getAnchorById } from "@/mocks/anchors";
import { formatViewers } from "@/utils/format";

interface PlayerDemoProps {
  live: Live;
}

export default function PlayerDemo({ live }: PlayerDemoProps) {
  const [playing, setPlaying] = useState(false);
  const [toast, setToast] = useState(false);
  const anchor = getAnchorById(live.anchorId);

  const handlePlay = () => {
    setToast(true);
    window.setTimeout(() => setToast(false), 2600);
    setPlaying((prev) => !prev);
  };

  return (
    <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
      <div className="relative aspect-video w-full bg-foreground-950">
        <img
          src={live.cover}
          alt={`${live.title} 直播封面`}
          title={`${live.title} 蜜梨直播`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-foreground-950/40" />

        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <button
            type="button"
            onClick={handlePlay}
            aria-label="播放直播"
            className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform hover:scale-105 md:h-20 md:w-20"
          >
            <i className={playing ? "ri-pause-fill text-3xl" : "ri-play-fill text-3xl"} />
          </button>
        </div>

        <div className="absolute left-3 top-3 flex items-center gap-2">
          <span className="flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
            <span className="h-1.5 w-1.5 rounded-full bg-background-50" />
            {live.status === "live" ? "直播中" : "已结束"}
          </span>
          <span className="rounded-full bg-background-50/20 px-3 py-1 text-xs font-medium text-background-50 backdrop-blur">
            高清 1080P
          </span>
        </div>

        <div className="absolute bottom-3 right-3 flex items-center gap-1 rounded-full bg-foreground-950/70 px-3 py-1 text-xs text-background-50">
          <i className="ri-eye-line text-sm" />
          {formatViewers(live.viewers)} 人在看
        </div>

        {toast && (
          <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2 rounded-md bg-foreground-950/90 px-4 py-2 text-xs text-background-50 md:left-3 md:right-auto">
            <i className="ri-information-line text-base text-primary-300" />
            演示效果，待后端接口接入真实直播流
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <span className="h-12 w-12 shrink-0 overflow-hidden rounded-full bg-secondary-200">
            <img
              src={anchor?.avatar}
              alt={`${anchor?.name} 头像`}
              className="h-full w-full object-cover object-top"
            />
          </span>
          <div>
            <p className="flex items-center gap-2 text-sm font-bold text-foreground-950">
              {anchor?.name}
              {anchor?.level === "vip" && (
                <span className="flex items-center gap-0.5 rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                  <i className="ri-vip-crown-2-line text-xs" />
                  VIP
                </span>
              )}
            </p>
            <p className="mt-0.5 text-xs text-foreground-500">
              <i className="ri-user-heart-line mr-1 text-primary-500" />
              {anchor ? `${(anchor.followers / 10000).toFixed(1)}万` : "-"} 粉丝关注
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 rounded-md bg-secondary-100 px-4 py-2 text-sm font-medium text-foreground-700">
            <i className="ri-live-line text-base text-primary-500" />
            演示播放器
          </span>
        </div>
      </div>
    </div>
  );
}